import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  reviewBills,
  createBatch,
} from "../../../services/panel/panelServices";
import { useAuth, ROLES } from "../../../context/AuthContext";
import toast from "../../../utils/toast";

const formatAmount = (amt) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

// "2026-11-04T00:00:00" → "4 Nov"
const formatDueDate = (value) => {
  if (!value) return "—";
  const d = new Date(String(value).slice(0, 10));
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Checker only views this screen; Maker and Fleet Owner create the batch
const CAN_CREATE_ROLES = [ROLES.MAKER, ROLES.FLEET_OWNER];

export default function BillReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // From the due calendar (many ids) or "Pay now" on Saved C/A (one account)
  const consumerAccountIDs =
    location.state?.consumerAccountIDs ||
    (location.state?.account ? [location.state.account.id] : []);

  const [isReviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (consumerAccountIDs.length) fetchReview();
  }, []);

  const fetchReview = async () => {
    setReviewLoading(true);
    try {
      const res = await reviewBills({ ConsumerAccountIDs: consumerAccountIDs });

      if (res?.statuscode === 1) {
        setReviewData(res);
      } else {
        setReviewData(null);
        toast.error(res?.msg);
      }
    } catch (err) {
      console.log(err.response?.data?.msg || "Failed to review bills", "error");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleCreateBatch = async () => {
    setCreating(true);
    try {
      // Server re-reads the amounts, so only the ids go
      const res = await createBatch({ ConsumerAccountIDs: consumerAccountIDs });

      if (res?.statuscode === 1) {
        toast.success(res?.msg);
        navigate("/approval", { state: { batchId: res?.batchID } });
      } else {
        toast.error(res?.msg);
        // A bill may have been locked or paid meanwhile — show the fresh state
        fetchReview();
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to create batch.");
    } finally {
      setCreating(false);
    }
  };

  const records = isReviewData?.records || [];
  const payableTotal = isReviewData?.payableTotal || 0;
  const canCreate = CAN_CREATE_ROLES.includes(user?.roleID);

  const approvalText =
    isReviewData?.approvalsRequired === 2
      ? `two approvals (Checker, then Fleet Owner), as it is above ${formatAmount(
          isReviewData?.dualApprovalAbove,
        )}`
      : "one approval from a Checker or the Fleet Owner";

  return (
    <>
      <div className="screen active" id="screen-review">
        <div className="page-head">
          <div>
            <h1>Bill review</h1>
            <div className="ph-sub">
              Check the selected bills and create a batch. It then waits for
              approval.
            </div>
          </div>
        </div>
        {!consumerAccountIDs.length ? (
          <div className="card">
            <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
              No bills selected. Pick bills from the due-date calendar first.
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => navigate("/due-date-calendar")}
            >
              Go to due-date calendar
            </button>
          </div>
        ) : (
          <>
            {isReviewData && (
              <div className="role-banner info" id="review-role-banner">
                <div className="avatar-sm">
                  {(user?.name || "?")
                    .split(" ")
                    .filter(Boolean)
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  {canCreate ? (
                    <>
                      <strong>
                        {user?.name} ({user?.roleName})
                      </strong>{" "}
                      will be recorded as the maker of this batch. At{" "}
                      {formatAmount(payableTotal)} it needs {approvalText}.
                    </>
                  ) : (
                    <>
                      You can view this list, but only a Maker or the Fleet
                      Owner can create the batch. At{" "}
                      {formatAmount(payableTotal)} it needs {approvalText}.
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="card">
              <div className="table-wrap">
                <table className="data-table" id="review-table">
                  <tbody>
                    <tr>
                      <th>Biller</th>
                      <th>Consumer number</th>
                      <th className="tright">Amount</th>
                      <th className="tright">Due date</th>
                    </tr>
                    {reviewLoading ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center" }}>
                          Loading...
                        </td>
                      </tr>
                    ) : records.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center" }}>
                          No bills found.
                        </td>
                      </tr>
                    ) : (
                      records.map((bill) => (
                        <tr
                          key={bill.consumerAccountID}
                          style={bill.isLocked ? { opacity: 0.55 } : undefined}
                        >
                          <td>
                            {bill.billerName}
                            {bill.nickname && (
                              <div
                                style={{
                                  fontSize: "10.5px",
                                  color: "var(--text-faint)",
                                }}
                              >
                                {bill.nickname}
                              </div>
                            )}
                          </td>
                          <td className="mono">{bill.accountNumber}</td>
                          <td className="tright mono">
                            {formatAmount(bill.amount)}
                          </td>
                          <td className="tright">
                            {formatDueDate(bill.dueDate)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: "1px solid var(--hair)",
                }}
              >
                <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
                  Selected total
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontWeight: 700,
                    fontSize: 19,
                  }}
                  id="review-total"
                >
                  {formatAmount(payableTotal)}
                </div>
              </div>
              {records.some((b) => b.isLocked) && (
                <div
                  id="review-locked-note"
                  style={{
                    fontSize: "11.5px",
                    color: "var(--text-faint)",
                    marginTop: 10,
                  }}
                >
                  Bills already in another batch are left out of the total and
                  of this batch.
                </div>
              )}
              {canCreate && (
                <button
                  className="btn btn-primary"
                  id="review-submit-btn"
                  style={{ marginTop: 14 }}
                  onClick={handleCreateBatch}
                  disabled={creating || reviewLoading || !payableTotal}
                >
                  {creating
                    ? "Creating batch..."
                    : "Create batch & send for approval"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
